import { Formik, FormikHelpers } from 'formik';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { FormModal, GFormFields, Text } from '@components';
import { getSSLFormConfig } from '@constants';
import { ApiEndpoint, Dictionaries, FormActionType, FormType, Translation, sslCertificateType } from '@enums';
import { useAppDispatch, useFormContent, useFormProcedure, useFormSubmissionStatus } from '@hooks';
import { submitFormRequest, useCdnSummary } from '@store';
import i18n from '@translations';
import type { DialogBase, DictionaryOptionItem, FormConfig, SSLConfigurationForm } from '@types';
import { checkSSLFormType, decrypt64Base, encrypt64Base, getOptionListFromCatalog, validateValueEqual } from '@utils';

export const SSLModal: FC<DialogBase> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const formType = FormType.CdnSSL;

  const currentForm = useFormContent({ formType });
  const currentProcedure = useFormProcedure(formType);
  const isSubmitting = useFormSubmissionStatus({ formType });

  const { domain } = useCdnSummary();

  const verifiedDomains = useMemo(
    () => domain.filter(({ cname_verified }) => cname_verified).map(({ domain }) => domain),
    [domain],
  );

  const { normalDomains } = useMemo(
    () => ({
      wildcardDomains: verifiedDomains.filter((d: string) => d.startsWith('*.')),
      normalDomains: verifiedDomains.filter((d: string) => !d.startsWith('*.')),
    }),
    [verifiedDomains],
  );

  const { t: tModal } = useTranslation(Translation.Configuration, { keyPrefix: 'modals.ssl' });

  const isEdit = validateValueEqual(currentProcedure, FormActionType.Edit);
  const isLetsEncrypt = validateValueEqual(currentForm?.type, sslCertificateType.letsEncrypt);

  const handleSubmit = (values: SSLConfigurationForm, helpers: FormikHelpers<SSLConfigurationForm>) => {
    const { type, domain, public_key, private_key, ...updatedValues } = values;

    const validDomains = domain.filter((d) => d.trim() !== '');

    const encryptedPrivateKey = encrypt64Base(private_key);
    const encryptedPublicKey = encrypt64Base(public_key);
    const formData = {
      ...updatedValues,
      type: type,
      domain: validDomains,
      public_key: encryptedPublicKey,
      private_key: encryptedPrivateKey,
    };
    dispatch(
      submitFormRequest({
        formType,
        formData: isEdit ? formData : { application_id, ...formData, ...checkSSLFormType(formData) },
        ...(isEdit && {
          params: {
            content_id: currentForm?.id,
          },
        }),
        globalOptions: {
          refetch_id: application_id,
        },
      }),
    );
  };

  const initialValues: SSLConfigurationForm = useMemo(
    () => ({
      name: currentForm?.name ?? '',
      type: currentForm?.type ?? normalDomains.length > 0 ? sslCertificateType.letsEncrypt : sslCertificateType.manual,
      public_key: decrypt64Base(currentForm?.public_key) ?? currentForm?.public_key ?? '',
      private_key: decrypt64Base(currentForm?.private_key) ?? currentForm?.private_key ?? '',
      domain: currentForm?.domain ?? [],
    }),
    [currentForm],
  );

  const SSLTypeOption: DictionaryOptionItem[] = useMemo(() => {
    const options = getOptionListFromCatalog({
      name: Dictionaries.SSLType,
      ...(normalDomains.length <= 0 && {
        disabledOptions: [sslCertificateType.letsEncrypt],
      }),
    });

    options.forEach((option) => {
      if (option.value === sslCertificateType.letsEncrypt && option.disabled) {
        option.label = `${option.label} (**${tModal('noVerifiedDomainAvailable')}**)`;
      }
    });

    return options;
  }, [normalDomains]);

  const formConfig: FormConfig = useMemo(
    () =>
      getSSLFormConfig({
        endpoint: ApiEndpoint.DEFAULT,
        isSubmitting,
        isEdit,
        isLetsEncrypt,
        SSLTypeOption,
      }),
    [isSubmitting, isEdit, isLetsEncrypt, SSLTypeOption, i18n.language],
  );

  const { inputList, validationSchema, label } = formConfig;

  if (!open || !inputList) return null;

  return (
    <Formik<SSLConfigurationForm>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ touched, errors }) => (
        <FormModal
          formType={formType}
          title={label?.title}
          description={label?.description}
          onClose={onClose}
          size="lg"
          open={open}
          confirmButton={{ children: label?.confirmButton }}
        >
          <GFormFields inputList={inputList} touched={touched} errors={errors} />

          {isEdit && isLetsEncrypt && (
            <Text
              $level={6}
              $customizeColor
              className="ring-1 ring-red-400 bg-red-50 px-2.5 py-1.5 rounded-md text-red-400"
            >
              {tModal('manualCertificateIssueNotice')}
            </Text>
          )}
        </FormModal>
      )}
    </Formik>
  );
};
